import { VerityAssessment } from "@verity/api-client/types";
import styles from "./TakeAssignmentPage.module.css";
import {
	CoreDividerWithText,
	CoreFileCard,
	CoreFileItem,
	CoreNoResults,
	CorePaper,
	CoreQrCode,
	coreToast,
	CoreUpload,
} from "@verity/components/core";
import { Typography } from "@mui/material";
import i18n from "../../../../i18n";
import {
	baseTransKey,
	extractFileFormats,
	extractVerityAssignmentInstructions,
	getAcceptedFileFormatDescription,
} from "../takeAssessmentHelper";
import InstructionsPreview from "../../assessments-page/verity-assessment/InstructionsPreview";
import { useCallback, useEffect, useMemo, useState } from "react";
import apiClient from "../../../../helpers/api-client";
import {
	getFileTooBigErrorMessage,
	isFileTooLarge,
} from "../../assessments-page/verity-assessment/verityAssessmentHelper";
import { getAcceptedFileExtString } from "../../../../helpers/file";
import { SharedTakeAssessmentProps } from "..";
import { ApiClientResponseError } from "@verity/api-client";
import CountdownTimer from "./CountdownTimer";
import { MAX_FILE_UPLOAD } from "../../upload-files/uploadFilesHelper";
import { useMobileLayout, useScrollIntoView } from "@verity/components";

type FileUploadError = "fileTooLarge" | "uploadFailed" | "maxFilesExceeded";

function getFileErrorMessage(error?: FileUploadError): string | undefined {
	if (!error) return undefined;

	switch (error) {
		case "fileTooLarge":
			return getFileTooBigErrorMessage();
		case "uploadFailed":
			return i18n.t(`${baseTransKey}.uploadFail`) as string;
		default:
			return i18n.t(`${baseTransKey}.uploadFail`) as string;
	}
}

type CoreFileItemWithError = CoreFileItem & {
	error?: FileUploadError;
	fromMobileUpload?: boolean;
};

type TakeAssignmentPageProps = {
	assessment: VerityAssessment;
} & SharedTakeAssessmentProps;

type AssignmentInfo = {
	instructions: string;
	formatsArray: string[];
	numFilesAlreadyUploaded: number;
};

export default function TakeAssignmentPage({
	assessment,
	quizName,
	proctoringSessionId,
	registerSubmitFn,
	timerParts,
	timerStatus,
}: TakeAssignmentPageProps) {
	const [instructorFiles, setInstructorFiles] = useState<CoreFileItem[]>([]);
	const [studentFiles, setStudentFiles] = useState<CoreFileItemWithError[]>([]);
	const [assignmentInfo, setAssignmentInfo] = useState<AssignmentInfo | null>(null);

	const uploadInstructions = getAcceptedFileFormatDescription(assignmentInfo?.formatsArray ?? []);
	const acceptAttribute = getAcceptedFileExtString(assignmentInfo?.formatsArray ?? []);

	const { ref, scrollIntoView } = useScrollIntoView<HTMLDivElement>();
	const isMobileLayout = useMobileLayout();

	const qrCodeValue = useMemo(() => {
		const searchParams = new URLSearchParams({ proctoringSessionId });
		return `${window.location.origin}/uploadFiles.html?${searchParams.toString()}`;
	}, [proctoringSessionId]);

	const hasFileFailedToUpload = useMemo(
		() => studentFiles.some((file) => file.error === "uploadFailed"),
		[studentFiles],
	);

	const canUploadFiles = useMemo(() => {
		return studentFiles.length < MAX_FILE_UPLOAD;
	}, [studentFiles]);

	const handleFileUpload = (files: File[]) => {
		const maxNewFilesAllowed = MAX_FILE_UPLOAD - studentFiles.length;
		const filesToAdd = files.slice(0, maxNewFilesAllowed);

		const newFileItems: CoreFileItemWithError[] = filesToAdd.map((file) => ({
			name: file.name,
			size: file.size,
			type: file.type,
			nativeFile: file,
			error: isFileTooLarge(file) ? "fileTooLarge" : undefined,
		}));

		setStudentFiles((prevFiles) => [...prevFiles, ...newFileItems]);
	};

	const handleFileRemove = (index: number) => {
		setStudentFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
	};

	const handleSubmit = useCallback(async () => {
		const updatedFiles = [...studentFiles];
		let error: FileUploadError | undefined = undefined;

		for (let i = 0; i < updatedFiles.length; i++) {
			const file = updatedFiles[i];
			if (file.error === "fileTooLarge") {
				error = "fileTooLarge";
				continue;
			}

			if (file.fromMobileUpload) {
				continue; // Skip files that have already been uploaded from mobile
			}

			const fileType = file.type ?? file.nativeFile?.type;
			try {
				if (!fileType || !file.nativeFile) {
					throw new Error("File type is required for upload");
				}

				const res = await apiClient.proctoringSession.getAssessmentSubmissionUploadUrl({
					proctoringSessionUuid: proctoringSessionId,
					fileName: file.name,
					mimeType: fileType,
					fileSize: file.size,
				});

				const url = res.uploadUrl;

				const uploadedResponse = await fetch(url, {
					method: "PUT",
					headers: {
						"Content-Type": fileType,
					},
					body: file.nativeFile,
				});

				if (!uploadedResponse.ok) {
					throw new Error(`File upload failed with status ${uploadedResponse.status}`);
				}
			} catch (e) {
				if (e instanceof ApiClientResponseError) {
					if (e.status === 413) updatedFiles[i].error = "fileTooLarge";
					if (e.status === 422) {
						error = "maxFilesExceeded";
						break; // No need to continue if we've hit the max files cap
					}
				} else {
					updatedFiles[i].error = "uploadFailed";
				}
				error = "uploadFailed";
			}
		}
		setStudentFiles(updatedFiles);

		if (error) {
			coreToast.dismiss();

			if (error === "maxFilesExceeded") {
				coreToast.warn(i18n.t(`${baseTransKey}.uploadFailMaxFilesToast`) as string);
			} else {
				coreToast.error(i18n.t(`${baseTransKey}.uploadFailToast`) as string);
			}
			// Scroll up to files
			scrollIntoView();
		}

		return { success: error === undefined };
	}, [studentFiles, proctoringSessionId, scrollIntoView]);

	useEffect(() => {
		registerSubmitFn(handleSubmit);
	}, [handleSubmit, registerSubmitFn]);

	useEffect(() => {
		void (async () => {
			try {
				const [instructorRes, questionRes, filesRes] = await Promise.all([
					apiClient.proctoringConfiguration.getAssessmentResources(assessment.quizId),
					apiClient.proctoringSession.getStudentQuizSessionData(proctoringSessionId),
					apiClient.proctoringSession.getAssessmentFileSubmissionStudent(proctoringSessionId),
				]);

				setInstructorFiles(instructorRes);
				setStudentFiles(filesRes.map((file) => ({ ...file, error: undefined, fromMobileUpload: true })));

				setAssignmentInfo({
					instructions: extractVerityAssignmentInstructions(questionRes),
					formatsArray: extractFileFormats(questionRes),
					numFilesAlreadyUploaded: filesRes.length,
				});
			} catch (e) {
				console.error(e);
			}
		})();
	}, [assessment.quizId, proctoringSessionId]);

	return (
		<CorePaper className={styles.assignmentCard}>
			<div className={styles.header}>
				<Typography className={styles.subtitle}>{i18n.t(`${baseTransKey}.assignmentSubtitle`)}</Typography>
				<CountdownTimer timerParts={timerParts} endMs={timerStatus.endTime} />
			</div>
			<div className={styles.assessmentInfoWrapper} ref={ref}>
				<Typography variant="h1" className={styles.quizName}>
					{quizName}
				</Typography>
				{assignmentInfo?.instructions && <InstructionsPreview instruction={assignmentInfo.instructions} />}
				{instructorFiles.length > 0 && (
					<div className={styles.instructorFilesWrapper}>
						{instructorFiles.map((file, index) => (
							<CoreFileCard key={index} file={file} showDownload />
						))}
					</div>
				)}
			</div>
			{studentFiles.length > 0 && (
				<div className={styles.studentFilesWrapper}>
					<Typography variant="h2" className={styles.uploadTitle}>
						{i18n.t(`${baseTransKey}.yourFiles`, { count: studentFiles.length })}
					</Typography>
					{hasFileFailedToUpload && (
						<div className={styles.uploadErrorWrapper}>
							<Typography className={styles.title}>{i18n.t(`${baseTransKey}.someFilesFailedTitle`)}</Typography>
							<Typography>{i18n.t(`${baseTransKey}.someFilesFailedDesc`)}</Typography>
						</div>
					)}
					{studentFiles.map((file, index) => (
						<CoreFileCard
							key={index}
							file={file}
							showOpenInNewTab
							onRemove={file.fromMobileUpload ? undefined : () => handleFileRemove(index)}
							errorMessage={getFileErrorMessage(file.error)}
							showCompleteMessage={Boolean(file.url) && !file.error}
						/>
					))}
				</div>
			)}
			{!canUploadFiles && (
				<CoreNoResults header={i18n.t(`${baseTransKey}.maxFilesUploadedTitle`) as string} variant="no-files">
					<Typography>{i18n.t(`${baseTransKey}.maxFilesUploadedDesc`, { count: MAX_FILE_UPLOAD })}</Typography>
				</CoreNoResults>
			)}
			{canUploadFiles && (
				<>
					<div className={styles.uploadInstructionsWrapper}>
						<Typography variant="h2" className={styles.uploadTitle}>
							{i18n.t(`${baseTransKey}.uploadTitle`)}
						</Typography>
						<Typography className={styles.uploadInstructions}>{i18n.t(`${baseTransKey}.uploadSubtitle`)}</Typography>
						<Typography className={styles.uploadInstructions}>{uploadInstructions}</Typography>
						<Typography className={styles.uploadInstructions}>
							{i18n.t(`${baseTransKey}.yourFilesDesc`, { count: MAX_FILE_UPLOAD })}
						</Typography>
					</div>
					<div className={styles.uploadWrapper}>
						<CoreUpload
							fileTypes={acceptAttribute}
							buttonText={i18n.t(`${baseTransKey}.uploadFile`) as string}
							onUpload={handleFileUpload}
							className={styles.uploadFilesContainer}
							multiple
						/>
						<CoreDividerWithText orientation={isMobileLayout ? "horizontal" : "vertical"} />
						<div className={styles.qrCodeContainer}>
							<CoreQrCode
								description={i18n.t(`${baseTransKey}.qrCodeTitle`) as string}
								ariaLabel={i18n.t(`${baseTransKey}.qrCodeTitle`) as string}
								qrCodeUrl={qrCodeValue}
								width={120}
								height={120}
								qrCornerSize={16}
							/>
							<Typography className={styles.qrCodeInstructions}>{i18n.t(`${baseTransKey}.qrCodeDesc`)}</Typography>
						</div>
					</div>
				</>
			)}
		</CorePaper>
	);
}
